import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import ZoneDetail from './pages/ZoneDetail';
import AskAgent from './pages/AskAgent';
import Reports from './pages/Reports';
import Tasks from './pages/Tasks';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Layout from './components/Layout';
import ThemeProvider from './components/ThemeProvider';
import NotificationManager from './components/NotificationManager';
import ErrorBoundary from './components/ErrorBoundary';
import { AppContextProvider } from './context/AppContext';
import { AgentContextProvider } from './context/AgentContext';
import { TaskContextProvider } from './context/TaskContext';
import { ZoneContextProvider } from './context/ZoneContext';
import { WeatherContextProvider } from './context/WeatherContext';
import { RecommendationContextProvider } from './context/RecommendationContext';
import { SettingsContextProvider } from './context/SettingsContext';
import { AdminAuthContextProvider, useAdminAuth } from './context/AdminAuthContext';
import { UserAuthContextProvider, useUserAuth } from './context/UserAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import AdminSettings from './pages/admin/Settings';
import './styles/index.css';

// Main application routes
const AppRoutes = () => {
  const userAuth = useUserAuth();
  const adminAuth = useAdminAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      
      {/* Admin Login Route - Must be outside the protected routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Main App Routes - Protected */}
      <Route path="/" element={
        <ProtectedRoute auth={userAuth}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Home />} />
        <Route path="zones/:zoneId" element={<ZoneDetail />} />
        <Route path="zones" element={<Home />} />
        <Route path="ask-agent" element={<AskAgent />} />
        <Route path="reports" element={<Reports />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="settings" element={<Settings />} />
        <Route path="dashboard" element={<Navigate to="/" replace />} />
      </Route>
      
      {/* Admin Routes - Protected */}
      <Route path="/admin/*" element={
        <ProtectedRoute auth={adminAuth} requireAdmin={true} redirectPath="/admin/login">
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      
      {/* Redirect any unmatched routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AppContextProvider>
      <SettingsContextProvider>
        <ThemeProvider>
          <ZoneContextProvider>
            <TaskContextProvider>
              <WeatherContextProvider>
                <RecommendationContextProvider>
                  <AgentContextProvider>
                    <UserAuthContextProvider>
                      <AdminAuthContextProvider>
                        <ErrorBoundary>
                          <div className="App">
                            <NotificationManager />
                            <AppRoutes />
                          </div>
                        </ErrorBoundary>
                      </AdminAuthContextProvider>
                    </UserAuthContextProvider>
                  </AgentContextProvider>
                </RecommendationContextProvider>
              </WeatherContextProvider>
            </TaskContextProvider>
          </ZoneContextProvider>
        </ThemeProvider>
      </SettingsContextProvider>
    </AppContextProvider>
  );
}

export default App;
